import React, { useState, useEffect, useRef } from 'react';
import './AlarmComponent.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faBell, faEdit } from '@fortawesome/free-solid-svg-icons';

function AlarmComponent() {
  const [alarms, setAlarms] = useState(() => {
    const savedAlarms = localStorage.getItem('alarms');
    return savedAlarms ? JSON.parse(savedAlarms) : [];
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newAlarm, setNewAlarm] = useState({
    hour: '07',
    minute: '00',
    period: 'AM',
    label: 'Good Morning',
    repeat: false,
    days: [],
    sound: 'Chimes',
    snooze: '10 minutes'
  });
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isAlarmRinging, setIsAlarmRinging] = useState(false);
  const [activeAlarm, setActiveAlarm] = useState(null);  // To track the currently ringing alarm
  const [snoozeOptionsVisible, setSnoozeOptionsVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // To track if the user is editing an alarm
  const [editIndex, setEditIndex] = useState(null);  // To track which alarm is being edited
  const alarmTimeout = useRef(null);
  const snoozeTimeout = useRef(null);

  useEffect(() => {
    localStorage.setItem('alarms', JSON.stringify(alarms));
  }, [alarms]);

  useEffect(() => {
    const checkAlarms = () => {
      const now = new Date();
      alarms.forEach(alarm => {
        if (alarm.active && !isAlarmRinging) {
          const alarmTime = new Date();
          const hour = parseInt(alarm.hour);
          const minute = parseInt(alarm.minute);
          alarmTime.setHours(alarm.period === 'AM' ? (hour % 12) : (hour % 12) + 12, minute, 0, 0);

          if (now >= alarmTime && now <= alarmTime.setSeconds(alarmTime.getSeconds() + 59)) {
            triggerAlarm(alarm);
          }
        }
      });
    };

    if (alarms.length > 0) {
      alarmTimeout.current = setInterval(checkAlarms, 1000);
    }

    return () => clearInterval(alarmTimeout.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarms, isAlarmRinging]);

  const triggerAlarm = (alarm) => {
    playSound(alarm.sound);
    setIsAlarmRinging(true);
    setActiveAlarm(alarm);
  };

  const stopAlarm = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    setIsAlarmRinging(false);
    setActiveAlarm(null);
    clearTimeout(snoozeTimeout.current);
  };

  const snoozeAlarm = (minutes) => {
    stopAlarm();
    const snoozeTime = minutes * 60 * 1000; // Convert minutes to milliseconds
    snoozeTimeout.current = setTimeout(() => {
      triggerAlarm(activeAlarm);
    }, snoozeTime);
    setSnoozeOptionsVisible(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    stopAlarm();
    setIsModalOpen(false);
    setIsEditing(false); // Reset editing state when modal is closed
    setEditIndex(null);
  };

  const handleInputChange = (field, value) => {
    setNewAlarm(prevAlarm => ({
      ...prevAlarm,
      [field]: value,
    }));
  };

  const toggleDay = (day) => {
    setNewAlarm(prevAlarm => ({
      ...prevAlarm,
      days: prevAlarm.days.includes(day)
        ? prevAlarm.days.filter(d => d !== day)
        : [...prevAlarm.days, day],
    }));
  };

  const playSound = (sound) => {
    stopAlarm(); // Stop any previously playing sound

    const soundPaths = {
      Chimes: '/assets/sounds/chimes.mp3',
      Xylophone: '/assets/sounds/xylophone.mp3',
      Chords: '/assets/sounds/chords.mp3',
      Tap: '/assets/sounds/tap.mp3',
      Jingle: '/assets/sounds/jingle.mp3',
      Transition: '/assets/sounds/transition.mp3',
      Descending: '/assets/sounds/descending.mp3',
      Bounce: '/assets/sounds/bounce.mp3',
      Echo: '/assets/sounds/echo.mp3'
    };

    const audio = new Audio(soundPaths[sound]);
    audio.loop = true;
    audio.play();
    setCurrentAudio(audio);
  };

  const saveAlarm = () => {
    if (isEditing && editIndex !== null) {
      // If editing, update the existing alarm
      setAlarms(prevAlarms => {
        const updatedAlarms = [...prevAlarms];
        updatedAlarms[editIndex] = { ...newAlarm, active: true };
        return updatedAlarms;
      });
    } else {
      // Otherwise, add a new alarm
      setAlarms([...alarms, { ...newAlarm, active: true }]);
    }
    
    setNewAlarm({
      hour: '07',
      minute: '00',
      period: 'AM',
      label: `Alarm (${alarms.length + 1})`,
      repeat: false,
      days: [],
      sound: 'Chimes',
      snooze: '10 minutes'
    });
    closeModal();
  };

  const toggleAlarm = (index) => {
    setAlarms(prevAlarms => {
      const updatedAlarms = [...prevAlarms];
      updatedAlarms[index].active = !updatedAlarms[index].active;
      return updatedAlarms;
    });
  };

  const deleteAlarm = (index) => {
    setAlarms(prevAlarms => prevAlarms.filter((_, i) => i !== index));
  };

  const editAlarm = (index) => {
    const alarmToEdit = alarms[index];
    setNewAlarm(alarmToEdit);
    setIsEditing(true);
    setEditIndex(index);
    setIsModalOpen(true);
  };

  const calculateTimeRemaining = (alarm) => {
    const now = new Date();
    let alarmTime = new Date();
    const hour = parseInt(alarm.hour);
    const minute = parseInt(alarm.minute);

    if (alarm.period === 'AM') {
      alarmTime.setHours(hour === 12 ? 0 : hour, minute, 0, 0);
    } else {
      alarmTime.setHours(hour === 12 ? 12 : hour + 12, minute, 0, 0);
    }

    if (alarmTime <= now) {
      alarmTime.setDate(alarmTime.getDate() + 1);
    }

    const timeDiff = alarmTime - now;

    const hours = Math.floor((timeDiff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((timeDiff / (1000 * 60)) % 60);
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    return `in ${days} days, ${hours} hours, ${minutes} minutes`;
  };

  return (
    <div className="alarm-container">
      <div className="alarm-list">
        {alarms.map((alarm, index) => (
          <div key={index} className="alarm-card">
            <div className="alarm-header">
              <div className="alarm-time">
                {alarm.hour}:{alarm.minute} {alarm.period}
              </div>
              <div className="alarm-actions">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={alarm.active || false}
                    onChange={() => toggleAlarm(index)}
                  />
                  <span className="slider"></span>
                </label>
                <button className="edit-button" onClick={() => editAlarm(index)}>
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button className="delete-button" onClick={() => deleteAlarm(index)}>
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
            <div className="alarm-remaining-time">
              {calculateTimeRemaining(alarm)}
            </div>
            <div className="alarm-label">
              {alarm.label}
            </div>
            <div className="alarm-days">
              {['Su', 'M', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <span
                  key={day}
                  className={`day ${alarm.days.includes(day) ? 'active' : ''}`}
                >
                  {day}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button className="floating-plus" onClick={openModal}>+</button>

      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h3>{isEditing ? 'Edit Alarm' : 'Add new alarm'}</h3>
            <div className="time-picker">
              <div className="time-input">
                <button onClick={() => handleInputChange('hour', (parseInt(newAlarm.hour) + 1).toString().padStart(2, '0'))}>▲</button>
                <input
                  type="text"
                  value={newAlarm.hour}
                  onChange={(e) => handleInputChange('hour', e.target.value)}
                />
                <button onClick={() => handleInputChange('hour', (parseInt(newAlarm.hour) - 1).toString().padStart(2, '0'))}>▼</button>
              </div>
              :
              <div className="time-input">
                <button onClick={() => handleInputChange('minute', (parseInt(newAlarm.minute) + 1).toString().padStart(2, '0'))}>▲</button>
                <input
                  type="text"
                  value={newAlarm.minute}
                  onChange={(e) => handleInputChange('minute', e.target.value)}
                />
                <button onClick={() => handleInputChange('minute', (parseInt(newAlarm.minute) - 1).toString().padStart(2, '0'))}>▼</button>
              </div>
              <div className="period-input">
                <button onClick={() => handleInputChange('period', newAlarm.period === 'AM' ? 'PM' : 'AM')}>▲</button>
                <input
                  type="text"
                  value={newAlarm.period}
                  readOnly
                />
                <button onClick={() => handleInputChange('period', newAlarm.period === 'AM' ? 'PM' : 'AM')}>▼</button>
              </div>
            </div>
            <div className="alarm-label-input">
              <input
                type="text"
                value={newAlarm.label}
                onChange={(e) => handleInputChange('label', e.target.value)}
              />
            </div>
            <div className="repeat-alarm">
              <input
                type="checkbox"
                checked={newAlarm.repeat}
                onChange={(e) => handleInputChange('repeat', e.target.checked)}
              />
              Repeat alarm
            </div>
            <div className="days-selector">
              {['Su', 'M', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div
                  key={day}
                  className={`day-button ${newAlarm.days.includes(day) ? 'selected' : ''}`}
                  onClick={() => toggleDay(day)}
                >
                  {day}
                </div>
              ))}
            </div>
            <div className="sound-selector">
              <label>Sound</label>
              <select
                value={newAlarm.sound}
                onChange={(e) => handleInputChange('sound', e.target.value)}
              >
                <option value="Chimes">Chimes</option>
                <option value="Xylophone">Xylophone</option>
                <option value="Chords">Chords</option>
                <option value="Tap">Tap</option>
                <option value="Jingle">Jingle</option>
                <option value="Transition">Transition</option>
                <option value="Descending">Descending</option>
                <option value="Bounce">Bounce</option>
                <option value="Echo">Echo</option>
              </select>
                <button className="play-sound" onClick={() => playSound(newAlarm.sound)}>
                    <span role="img" aria-label="play sound">▶️</span>
                </button>

            </div>
            <div className="snooze-selector">
              <label>Snooze</label>
              <select
                value={newAlarm.snooze}
                onChange={(e) => handleInputChange('snooze', e.target.value)}
              >
                <option value="5 minutes">5 minutes</option>
                <option value="10 minutes">10 minutes</option>
                <option value="15 minutes">15 minutes</option>
                <option value="30 minutes">30 minutes</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="save-button" onClick={saveAlarm}>{isEditing ? 'Update' : 'Save'}</button>
              <button className="cancel-button" onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {isAlarmRinging && (
        <div className="alarm-notification">
          <div className="alarm-notification-content">
            <FontAwesomeIcon icon={faBell} className="bell-icon" />
            <p>Alarm is ringing!</p>
            <div className="notification-actions">
              <button className="dismiss-button" onClick={stopAlarm}>Dismiss</button>
              <button className="snooze-button" onClick={() => setSnoozeOptionsVisible(!snoozeOptionsVisible)}>Snooze</button>
            </div>
            {snoozeOptionsVisible && (
              <div className="snooze-options">
                <button onClick={() => snoozeAlarm(5)}>5 minutes</button>
                <button onClick={() => snoozeAlarm(10)}>10 minutes</button>
                <button onClick={() => snoozeAlarm(15)}>15 minutes</button>
                <button onClick={() => snoozeAlarm(30)}>30 minutes</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AlarmComponent;
